import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, Link } from 'react-router-dom'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { useApi } from '../hooks/useApi'

const DashboardConcept = () => {
  const location = useLocation()
  const [stats, setStats] = useState(null)
  const [onboardingData, setOnboardingData] = useState(null)
  const [communityActivity, setCommunityActivity] = useState([])
  const [showDetailedView, setShowDetailedView] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [showCheckInModal, setShowCheckInModal] = useState(false)
  const [checkInProgress, setCheckInProgress] = useState(null)
  const [checkInForm, setCheckInForm] = useState({
    physicalGoalScore: 5,
    overallWellbeingScore: 5,
    notes: ''
  })
  const [submittingCheckIn, setSubmittingCheckIn] = useState(false)
  const { loading, error } = useApi()

  useEffect(() => {
    loadDashboardData()
    loadOnboardingData()
    loadCommunityActivity()
    loadCheckInProgress()
  }, [])

  const loadDashboardData = async () => {
    // Example API call
    // const data = await get('/api/dashboard/stats')
    // setStats(data)
    
    // Mock data for demonstration
    setTimeout(() => {
      setStats({
        totalActivities: 42,
        weeklyGoal: 75,
        completedGoals: 12,
        streakDays: 7,
      })
    }, 1000)
  }

  const loadOnboardingData = () => {
    // Load onboarding data from localStorage or location state
    const locationData = location.state
    const storedData = JSON.parse(localStorage.getItem('onboardingData') || '{}')
    
    // Combine location state and stored data
    const userData = {
      ...storedData,
      ...locationData,
    }
    
    setOnboardingData(userData)
    console.log('User journey data:', userData)
  }

  const loadCheckInProgress = async () => {
    // In production: const data = await get('/api/checkins/progress')
    // Mock data for demonstration
    setTimeout(() => {
      const mockProgress = {
        hasData: true,
        totalCheckIns: 8,
        checkIns: [
          { id: 1, physicalGoalScore: 3, overallWellbeingScore: 4, createdAt: new Date(Date.now() - 56 * 24 * 60 * 60 * 1000) },
          { id: 2, physicalGoalScore: 4, overallWellbeingScore: 4, createdAt: new Date(Date.now() - 49 * 24 * 60 * 60 * 1000) },
          { id: 3, physicalGoalScore: 4, overallWellbeingScore: 5, createdAt: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000) },
          { id: 4, physicalGoalScore: 5, overallWellbeingScore: 5, createdAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000) },
          { id: 5, physicalGoalScore: 5, overallWellbeingScore: 6, createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000) },
          { id: 6, physicalGoalScore: 6, overallWellbeingScore: 6, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000) },
          { id: 7, physicalGoalScore: 7, overallWellbeingScore: 7, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) },
          { id: 8, physicalGoalScore: 7, overallWellbeingScore: 8, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
        ],
        improvement: {
          physical: 4,
          wellbeing: 4,
          physicalPercentage: 133.3,
          wellbeingPercentage: 100.0,
        },
        averageScores: {
          physical: 5.1,
          wellbeing: 5.6,
        },
        firstCheckIn: { physicalGoalScore: 3, overallWellbeingScore: 4 },
        latestCheckIn: { physicalGoalScore: 7, overallWellbeingScore: 8 },
      }
      setCheckInProgress(mockProgress)
    }, 1200)
  }

  const handleCheckInSubmit = async (e) => {
    e.preventDefault()
    setSubmittingCheckIn(true)
    
    try {
      // In production: await post('/api/checkins', checkInForm)
      // Mock submission
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Reload progress data
      loadCheckInProgress()
      
      // Close modal and reset form
      setShowCheckInModal(false)
      setCheckInForm({
        physicalGoalScore: 5,
        overallWellbeingScore: 5,
        notes: ''
      })
      
      // Show success message (you could add a toast notification here)
      console.log('Check-in submitted successfully!')
    } catch (error) {
      console.error('Error submitting check-in:', error)
    } finally {
      setSubmittingCheckIn(false)
    }
  }

  const loadCommunityActivity = async () => {
    // Mock community activity data
    // In production, this would be an API call: const data = await get('/api/community/activity')
    setTimeout(() => {
      setCommunityActivity([
        {
          id: 1,
          userId: 'user_1',
          userName: 'Priya Sharma',
          activityType: 'yoga',
          activityName: 'Morning Vinyasa Flow',
          duration: 45,
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          streak: 12,
          comment: 'Feeling amazing after this session! My flexibility is really improving. 🌟',
          likes: 8,
          totalActivities: 67,
        },
        {
          id: 2,
          userId: 'user_2',
          userName: 'Arjun Singh',
          activityType: 'meditation',
          activityName: 'Guided Mindfulness Meditation',
          duration: 20,
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
          streak: 30,
          comment: 'Day 30! This practice has completely changed my perspective on stress management.',
          likes: 15,
          totalActivities: 145,
        },
        {
          id: 3,
          userId: 'user_3',
          userName: 'Kavya Reddy',
          activityType: 'yoga',
          activityName: 'Evening Yin Yoga',
          duration: 60,
          timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // Yesterday
          streak: 7,
          comment: 'Perfect way to wind down. My lower back feels so much better! 💆‍♀️',
          likes: 12,
          totalActivities: 43,
        },
        {
          id: 4,
          userId: 'user_4',
          userName: 'Rohan Patel',
          activityType: 'meditation',
          activityName: 'Breath Work Session',
          duration: 15,
          timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 - 6 * 60 * 60 * 1000), // Yesterday
          streak: 21,
          comment: 'Short but powerful session before my big presentation. Feeling centered and focused. 🎯',
          likes: 6,
          totalActivities: 89,
        },
        {
          id: 5,
          userId: 'user_5',
          userName: 'Ananya Iyer',
          activityType: 'yoga',
          activityName: 'Restorative Yoga Class',
          duration: 50,
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
          streak: 45,
          comment: 'Attended the community class today. Love connecting with everyone! 🙏',
          likes: 20,
          totalActivities: 203,
        },
        {
          id: 6,
          userId: 'user_6',
          userName: 'Vikram Kumar',
          activityType: 'meditation',
          activityName: 'Body Scan Meditation',
          duration: 30,
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 - 3 * 60 * 60 * 1000), // 2 days ago
          streak: 5,
          comment: 'Still building my consistency, but already noticing better sleep quality. 😴',
          likes: 9,
          totalActivities: 18,
        },
      ])
    }, 800)
  }

  const handleViewMemberDetails = (activity) => {
    setSelectedMember(activity)
    setShowDetailedView(true)
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const diff = now - timestamp
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days > 1) return `${days} days ago`
    if (days === 1) return 'Yesterday'
    if (hours > 1) return `${hours} hours ago`
    return 'Just now'
  }

  const getActivityIcon = (activityType) => {
    return activityType === 'yoga' ? '🧘' : '🧘‍♀️'
  }

  // Generate gradient colors based on name
  const getGradientColors = (name) => {
    const gradients = [
      { from: 'from-purple-500', to: 'to-pink-500' },
      { from: 'from-blue-500', to: 'to-cyan-500' },
      { from: 'from-indigo-500', to: 'to-purple-500' },
      { from: 'from-orange-500', to: 'to-red-500' },
      { from: 'from-green-500', to: 'to-teal-500' },
      { from: 'from-yellow-500', to: 'to-orange-500' },
      { from: 'from-pink-500', to: 'to-rose-500' },
      { from: 'from-violet-500', to: 'to-fuchsia-500' },
    ]
    // Use name hash to pick consistent gradient
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return gradients[hash % gradients.length]
  }

  // Avatar component with first letter and gradient
  const Avatar = ({ name, size = 'text-3xl' }) => {
    const firstLetter = name.charAt(0).toUpperCase()
    const gradient = getGradientColors(name)
    const sizeClasses = {
      'text-3xl': 'w-12 h-12 text-xl',
      'text-4xl': 'w-16 h-16 text-2xl',
      'text-6xl': 'w-24 h-24 text-4xl',
    }
    const sizeClass = sizeClasses[size] || sizeClasses['text-3xl']
    
    return (
      <div className={`${sizeClass} rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} flex items-center justify-center text-white font-bold shadow-md`}>
        {firstLetter}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20">
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-600">Error loading dashboard: {error}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="bg-cream min-h-screen py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Dashboard</h1>

          {/* Personalized Welcome Banner based on user certainty */}
          {onboardingData?.userCertainty && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8"
            >
              <Card className={`${
                onboardingData.userCertainty === 'unsure' 
                  ? 'bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200' 
                  : 'bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200'
              }`}>
                {onboardingData.userCertainty === 'unsure' ? (
                  <div>
                    <h2 className="text-2xl font-bold text-primary-900 mb-2">
                      🌟 Welcome to Your Exploratory Journey!
                    </h2>
                    <p className="text-primary-700 mb-4">
                      We&apos;ll help you discover which wellness practices resonate with you. 
                      Your dashboard is tailored to introduce you to various modalities - 
                      yoga and meditation.
                    </p>
                    <div className="flex gap-3">
                      <Link to="/facilitators">
                        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                          Start Discovery Session
                        </button>
                      </Link>
                      <button className="px-4 py-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors border border-primary-200">
                        View Recommendations
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-2xl font-bold text-primary-900 mb-2">
                      🎯 Welcome to Your Focused Journey!
                    </h2>
                    <p className="text-primary-700 mb-4">
                      Great choice! You&apos;ve selected {onboardingData.wellnessInterest} as your primary practice. 
                      Your dashboard is personalized to help you deepen your practice and track your progress.
                    </p>
                    <div className="flex gap-3">
                      <Link to="/facilitators">
                        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                          Begin Your Practice
                        </button>
                      </Link>
                      <button className="px-4 py-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors border border-primary-200">
                        Set Goals
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            </motion.div>
          )}

          {/* Check-In Section - Weekly Progress Tracking */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-primary-900 mb-2">
                    📋 Weekly Check-In
                  </h2>
                  <p className="text-primary-700 text-sm">
                    Track your progress and see how your wellness journey is improving over time
                  </p>
                </div>
                  <button
                  onClick={() => setShowCheckInModal(true)}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-md hover:shadow-lg"
                >
                  + New Check-In
                </button>
              </div>

              {checkInProgress?.hasData ? (
                <div className="space-y-6">
                  {/* Latest Scores Display */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-primary-100">
                      <p className="text-sm font-medium text-gray-600 mb-3">
                        {onboardingData?.targetAilment 
                          ? `${onboardingData.targetAilment.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Score`
                          : 'Latest Physical Goal Score'}
                      </p>
                      <div className="flex items-center space-x-3">
                        <div className="relative w-16 h-16">
                          <svg className="w-16 h-16 transform -rotate-90">
                            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200" />
                            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="6" fill="none"
                              strokeDasharray={`${(checkInProgress.latestCheckIn.physicalGoalScore / 10) * 176} 176`}
                              className="text-primary-600" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900">
                              {checkInProgress.latestCheckIn.physicalGoalScore}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {checkInProgress.latestCheckIn.physicalGoalScore}/10
                          </p>
                          {checkInProgress.improvement.physical > 0 && (
                            <p className="text-sm text-green-600 flex items-center">
                              <span className="mr-1">↑</span> +{checkInProgress.improvement.physical} from start
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-primary-200">
                      <p className="text-sm font-medium text-gray-600 mb-3">Latest Overall Wellbeing</p>
                      <div className="flex items-center space-x-3">
                        <div className="relative w-16 h-16">
                          <svg className="w-16 h-16 transform -rotate-90">
                            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200" />
                            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="6" fill="none"
                              strokeDasharray={`${(checkInProgress.latestCheckIn.overallWellbeingScore / 10) * 176} 176`}
                              className="text-primary-500" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900">
                              {checkInProgress.latestCheckIn.overallWellbeingScore}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {checkInProgress.latestCheckIn.overallWellbeingScore}/10
                          </p>
                          {checkInProgress.improvement.wellbeing > 0 && (
                            <p className="text-sm text-green-600 flex items-center">
                              <span className="mr-1">↑</span> +{checkInProgress.improvement.wellbeing} from start
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-primary-100">
                      <p className="text-sm font-medium text-gray-600 mb-3">Progress Summary</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Total Check-ins:</span>
                          <span className="font-bold text-gray-900">{checkInProgress.totalCheckIns}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Avg Physical:</span>
                          <span className="font-bold text-primary-600">{checkInProgress.averageScores.physical}/10</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Avg Wellbeing:</span>
                          <span className="font-bold text-primary-500">{checkInProgress.averageScores.wellbeing}/10</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Check-Ins Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Start tracking your progress by completing your first check-in
                  </p>
                  <button
                    onClick={() => setShowCheckInModal(true)}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Complete Your First Check-In
                  </button>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card delay={0.1}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm uppercase tracking-wide mb-1">
                    Total Activities
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats?.totalActivities || 0}
                  </p>
                </div>
                <div className="text-4xl">🏃</div>
              </div>
            </Card>

            <Card delay={0.2}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm uppercase tracking-wide mb-1">
                    Weekly Goal
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats?.weeklyGoal || 0}%
                  </p>
                </div>
                <div className="text-4xl">🎯</div>
              </div>
            </Card>

            <Card delay={0.3}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm uppercase tracking-wide mb-1">
                    Completed Goals
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats?.completedGoals || 0}
                  </p>
                </div>
                <div className="text-4xl">✅</div>
              </div>
            </Card>

            <Card delay={0.4}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm uppercase tracking-wide mb-1">
                    Current Streak
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats?.streakDays || 0} days
                  </p>
                </div>
                <div className="text-4xl">🔥</div>
              </div>
            </Card>
          </div>

          {/* Community Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" delay={0.5}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Activity from Your Community
                </h2>
                <button 
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  onClick={() => setShowDetailedView(true)}
                >
                  View All Members →
                </button>
              </div>
              <div className="space-y-4">
                {communityActivity.slice(0, 4).map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-white hover:bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-primary-200 transition-all cursor-pointer"
                    onClick={() => handleViewMemberDetails(activity)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <Avatar name={activity.userName} size="text-3xl" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-semibold text-gray-900">{activity.userName}</p>
                            <span className="text-gray-400">·</span>
                            <span className="text-sm text-gray-500">{getTimeAgo(activity.timestamp)}</span>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-lg">{getActivityIcon(activity.activityType)}</span>
                            <div>
                              <p className="font-medium text-gray-800">{activity.activityName}</p>
                              <p className="text-sm text-gray-600">{activity.duration} minutes</p>
                            </div>
                          </div>
                          {activity.comment && (
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 mb-2">
                              {activity.comment}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="flex items-center space-x-1 text-orange-600">
                              <span>🔥</span>
                              <span className="font-medium">{activity.streak} day streak</span>
                            </div>
                            <div className="flex items-center space-x-1 text-pink-600">
                              <span>❤️</span>
                              <span>{activity.likes}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <button className="text-gray-400 group-hover:text-indigo-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>

            {/* <Card delay={0.6}>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="space-y-3">
                <button className="w-full btn-primary text-left">
                  + Log Activity
                </button>
                <button className="w-full btn-secondary text-left">
                  + Set New Goal
                </button>
                <button className="w-full btn-secondary text-left">
                  📊 View Reports
                </button>
              </div>
            </Card> */}
          </div>
        </motion.div>

        {/* Check-In Modal */}
        <AnimatePresence>
          {showCheckInModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => !submittingCheckIn && setShowCheckInModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">
                    Complete Your Check-In
                  </h2>
                  <button
                    onClick={() => !submittingCheckIn && setShowCheckInModal(false)}
                    disabled={submittingCheckIn}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <form onSubmit={handleCheckInSubmit} className="p-6 space-y-6">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <p className="text-sm text-primary-800">
                      <strong>💡 Tip:</strong> Be honest with your scores. This helps you track real progress over time!
                    </p>
                  </div>

                  {/* Physical Goal Score */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {onboardingData?.targetAilment 
                        ? `${onboardingData.targetAilment.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Score (1-10)`
                        : 'Physical Goal Score (1-10)'}
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      {onboardingData?.targetAilment
                        ? `How would you rate your ${onboardingData.targetAilment.split('-').join(' ')} today? Consider strength, mobility, and comfort in this area.`
                        : 'How would you rate your physical wellness today? Consider your energy, strength, and physical comfort.'}
                    </p>
                    <div className="space-y-3">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={checkInForm.physicalGoalScore}
                        onChange={(e) => setCheckInForm({ ...checkInForm, physicalGoalScore: parseInt(e.target.value) })}
                        className="w-full h-2 bg-primary-200 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #466C47 0%, #466C47 ${(checkInForm.physicalGoalScore - 1) * 11.11}%, #e1ebe1 ${(checkInForm.physicalGoalScore - 1) * 11.11}%, #e1ebe1 100%)`
                        }}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Poor</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-3xl font-bold text-primary-600">{checkInForm.physicalGoalScore}</span>
                          <span className="text-sm text-gray-500">/10</span>
                        </div>
                        <span className="text-sm text-gray-500">Excellent</span>
                      </div>
                    </div>
                  </div>

                  {/* Overall Wellbeing Score */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Overall Wellbeing Score (1-10)
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      How would you rate your overall sense of wellbeing? Consider your mental, emotional, and spiritual state.
                    </p>
                    <div className="space-y-3">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={checkInForm.overallWellbeingScore}
                        onChange={(e) => setCheckInForm({ ...checkInForm, overallWellbeingScore: parseInt(e.target.value) })}
                        className="w-full h-2 bg-primary-200 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #466C47 0%, #466C47 ${(checkInForm.overallWellbeingScore - 1) * 11.11}%, #e1ebe1 ${(checkInForm.overallWellbeingScore - 1) * 11.11}%, #e1ebe1 100%)`
                        }}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Poor</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-3xl font-bold text-primary-600">{checkInForm.overallWellbeingScore}</span>
                          <span className="text-sm text-gray-500">/10</span>
                        </div>
                        <span className="text-sm text-gray-500">Excellent</span>
                      </div>
                    </div>
                  </div>

                  {/* Optional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={checkInForm.notes}
                      onChange={(e) => setCheckInForm({ ...checkInForm, notes: e.target.value })}
                      placeholder="Any thoughts or observations about your practice this week?"
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => !submittingCheckIn && setShowCheckInModal(false)}
                      disabled={submittingCheckIn}
                      className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingCheckIn}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
                    >
                      {submittingCheckIn ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        'Submit Check-In'
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Detailed Community View Modal */}
        <AnimatePresence>
          {showDetailedView && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowDetailedView(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">
                    Community Members & Activity
                  </h2>
                  <button
                    onClick={() => setShowDetailedView(false)}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
                  {selectedMember ? (
                    // Single Member Detail View
                    <div>
                      <button
                        onClick={() => setSelectedMember(null)}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
                      >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to All Members
                      </button>
                      
                      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-6 mb-6">
                        <div className="flex items-start space-x-4">
                          <Avatar name={selectedMember.userName} size="text-6xl" />
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedMember.userName}</h3>
                            <div className="grid grid-cols-3 gap-4 mt-4">
                              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3">
                                <p className="text-sm text-gray-600">Current Streak</p>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-2xl">🔥</span>
                                  <span className="text-2xl font-bold text-orange-600">{selectedMember.streak}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">days</p>
                              </div>
                              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3">
                                <p className="text-sm text-gray-600">Total Activities</p>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-2xl">✨</span>
                                    <span className="text-2xl font-bold text-primary-600">{selectedMember.totalActivities}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">completed</p>
                              </div>
                              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3">
                                <p className="text-sm text-gray-600">Community Love</p>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-2xl">❤️</span>
                                  <span className="text-2xl font-bold text-pink-600">{selectedMember.likes}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">reactions</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h4 className="text-lg font-bold text-gray-900">Recent Activity</h4>
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <span className="text-2xl">{getActivityIcon(selectedMember.activityType)}</span>
                            <div>
                              <p className="font-semibold text-gray-900">{selectedMember.activityName}</p>
                              <p className="text-sm text-gray-600">{selectedMember.duration} minutes · {getTimeAgo(selectedMember.timestamp)}</p>
                            </div>
                          </div>
                          {selectedMember.comment && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-gray-800">{selectedMember.comment}</p>
                            </div>
                          )}
                        </div>

                        {/* Mock additional activities */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <span className="text-2xl">🧘‍♀️</span>
                            <div>
                              <p className="font-semibold text-gray-900">Previous Session</p>
                              <p className="text-sm text-gray-600">30 minutes · 3 days ago</p>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-gray-800">Building consistency one day at a time. Grateful for this practice! 🙏</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // All Members List View
                    <div className="space-y-4">
                      {communityActivity.map((activity, index) => (
                        <motion.div
                          key={activity.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white border border-gray-200 hover:border-primary-300 rounded-xl p-5 transition-all cursor-pointer hover:shadow-md"
                          onClick={() => setSelectedMember(activity)}
                        >
                          <div className="flex items-start space-x-4">
                            <Avatar name={activity.userName} size="text-4xl" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-gray-900">{activity.userName}</h3>
                                <span className="text-sm text-gray-500">{getTimeAgo(activity.timestamp)}</span>
                              </div>
                              
                              <div className="grid grid-cols-3 gap-3 mb-3">
                                <div className="bg-orange-50 rounded-lg p-2">
                                  <p className="text-xs text-gray-600">Streak</p>
                                  <div className="flex items-center space-x-1">
                                    <span>🔥</span>
                                    <span className="font-bold text-orange-600">{activity.streak}</span>
                                  </div>
                                </div>
                                <div className="bg-primary-50 rounded-lg p-2">
                                  <p className="text-xs text-gray-600">Activities</p>
                                  <div className="flex items-center space-x-1">
                                    <span>✨</span>
                                    <span className="font-bold text-primary-600">{activity.totalActivities}</span>
                                  </div>
                                </div>
                                <div className="bg-pink-50 rounded-lg p-2">
                                  <p className="text-xs text-gray-600">Likes</p>
                                  <div className="flex items-center space-x-1">
                                    <span>❤️</span>
                                    <span className="font-bold text-pink-600">{activity.likes}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2 mb-2 bg-gray-50 rounded-lg p-2">
                                <span className="text-lg">{getActivityIcon(activity.activityType)}</span>
                                <div>
                                  <p className="font-medium text-gray-800 text-sm">{activity.activityName}</p>
                                  <p className="text-xs text-gray-600">{activity.duration} minutes</p>
                                </div>
                              </div>

                              {activity.comment && (
                                <p className="text-sm text-gray-700 bg-primary-50 rounded-lg p-3 italic">
                                  &ldquo;{activity.comment}&rdquo;
                                </p>
                              )}
                            </div>
                            <button className="text-gray-400 hover:text-primary-600 transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default DashboardConcept

